import React from 'react';
import { CheckCircle } from 'lucide-react';
import usePrices from '../../hooks/usePrices';
import { PAGES, serverPath } from '../../site/routes';
import { BANKS, BUY_STEPS, FAQ, faqJsonLd } from '../../site/content';
import {
  usePageSeo, Breadcrumbs, PageHeader, Section, Steps, ActionButtons, ArrowLink, TextLink, FaqList, CtaBand, LinkCards
} from '../../site/ui';
import { formatMad } from '../../data/servers';

const BREADCRUMBS = [
  { label: 'Accueil', to: PAGES.home },
  { label: 'Achat Kamas Maroc', to: PAGES.buyGuide },
];

const GUIDE_FAQ = [FAQ[0], FAQ[4], FAQ[5], FAQ[7]];

export default function BuyGuidePage() {
  const { servers } = usePrices();
  const cheapest = [...servers].sort((a, b) => a.buy - b.buy).slice(0, 6);

  usePageSeo({
    title: 'Achat Kamas Maroc : acheter des Kamas Dofus en DH | AV Kamas',
    description: "Achat Kamas au Maroc : payez en dirhams par virement (CIH, Attijariwafa, Barid Bank…) et recevez vos Kamas Dofus, Rétro ou Touch en jeu.",
    path: PAGES.buyGuide,
    breadcrumbs: BREADCRUMBS,
    jsonLd: [faqJsonLd(GUIDE_FAQ)],
  });

  return (
    <main className="bg-gray-950 text-white">
      <Breadcrumbs items={BREADCRUMBS} />
      <PageHeader
        kicker="Guide d'achat"
        title={<>Achat Kamas Maroc : <span className="text-yellow-400">acheter des Kamas en dirhams</span></>}
        intro="L'achat de Kamas au Maroc devient simple : vous payez en dirhams depuis votre banque marocaine et recevez vos Kamas directement sur votre personnage, sur n'importe quel serveur Dofus, Dofus Rétro ou Dofus Touch."
      >
        <ActionButtons buyLabel="Commencer un achat" sellLabel="Vendre des Kamas" className="mt-5" />
      </PageHeader>

      <Section title="Comment acheter des Kamas en 4 étapes" className="pt-0 md:pt-0">
        <Steps steps={BUY_STEPS} />
      </Section>

      <Section className="bg-gray-900/40">
        <div className="grid md:grid-cols-2 gap-6 md:gap-10 text-sm sm:text-base text-gray-300 leading-relaxed">
          <div>
            <h2 className="text-lg sm:text-2xl font-bold text-white mb-2">Pourquoi acheter vos Kamas chez AV Kamas ?</h2>
            <p className="mb-2">
              Nos prix d'<strong>achat Kamas</strong> suivent le marché en continu et restent sous ceux de TryAndJudge sur chaque serveur,
              jusqu'à 1 DH de moins par million sur Kelerog, Boune ou Tiliwan. Consultez le{' '}
              <TextLink to={PAGES.comparison}>comparatif des prix</TextLink>.
            </p>
            <p>
              Vous gardez la main à chaque étape : suivi de commande dans votre espace, discussion avec le support et envoi de votre
              reçu de virement en image ou en PDF.
            </p>
          </div>
          <div>
            <h2 className="text-lg sm:text-2xl font-bold text-white mb-2">Paiement accepté pour l'achat de Kamas</h2>
            <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5 mb-3">
              {BANKS.map(bank => (
                <li key={bank} className="flex items-center gap-1.5 text-xs sm:text-sm"><CheckCircle size={14} className="text-green-400 flex-shrink-0" />{bank}</li>
              ))}
            </ul>
            <ArrowLink to={PAGES.buy}>Commencer un achat de Kamas</ArrowLink>
          </div>
        </div>
      </Section>

      <Section
        title="Les Kamas les moins chers aujourd'hui"
        intro="Les serveurs où l'achat de Kamas coûte le moins cher en ce moment. Tous les autres tarifs sont sur la page des prix."
      >
        <LinkCards
          links={cheapest.map(s => ({ to: serverPath(s), title: `Acheter des Kamas ${s.name}`, text: `${formatMad(s.buy)} le million · ${s.type}` }))}
        />
        <div className="mt-4"><ArrowLink to={PAGES.prices}>Voir tous les prix des Kamas</ArrowLink></div>
      </Section>

      <Section title="Questions sur l'achat de Kamas" className="bg-gray-900/40">
        <FaqList items={GUIDE_FAQ} />
        <div className="mt-4"><ArrowLink to={PAGES.faq}>Toutes les questions fréquentes</ArrowLink></div>
      </Section>

      <CtaBand title="Prêt à acheter vos Kamas ?" text="Choisissez votre serveur, payez en dirhams et suivez votre commande dans le chat." />
    </main>
  );
}
